// Location: src/components/pdf/PlanSummaryDocument.tsx

import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { UserProfile } from '../../types';

interface PlanSummaryDocumentProps {
  // [Props kept exactly the same]
  summary: {
    start_weight: number; end_weight: number; weight_change: number; goal_weight: number;
    goal_achieved: boolean; days_completed: number; total_days: number; completion_percentage: number;
    meals_logged: number; total_meals: number; meal_adherence_percentage: number;
    avg_calories: number; avg_protein: number; avg_carbs: number; avg_fat: number;
    weekly_stats: any[]; best_days: any[]; worst_days: any[]; insights: any[];
    achievements_unlocked: any[]; generated_at: string;
  };
  plan: {
    title: string; goal: string; plan_start_date: string; duration_days: number;
  };
  userProfile: UserProfile
  language: 'de' | 'en';
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#0A0A0A',
    padding: 40,
    color: '#D1D5DB',
  },
  coverPage: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  title: {
    fontSize: 42,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: 'light',
    letterSpacing: -1,
  },
  subtitle: {
    fontSize: 14,
    color: '#EAB308',
    textAlign: 'center',
    marginBottom: 40,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  preparedFor: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 40,
  },
  infoCard: {
    backgroundColor: '#111111',
    padding: 24,
    borderRadius: 16,
    marginBottom: 15,
    border: '1px solid #222222',
    width: '85%',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    borderBottom: '1px solid #1A1A1A',
    paddingBottom: 8,
  },
  infoLabel: {
    fontSize: 10,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  infoValue: {
    fontSize: 12,
    color: '#FFFFFF',
  },
  footer: {
    fontSize: 9,
    color: '#4B5563',
    textAlign: 'center',
    position: 'absolute',
    bottom: 40,
    left: 40,
    right: 40,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  sectionTitle: {
    fontSize: 20,
    color: '#FFFFFF',
    marginBottom: 20,
    fontWeight: 'light',
    borderBottom: '1px solid #222222',
    paddingBottom: 10,
  },
  highlightBox: {
    backgroundColor: '#111111',
    border: '1px solid #EAB308',
    padding: 20,
    marginTop: 20,
    borderRadius: 12,
  },
  highlightText: {
    fontSize: 12,
    color: '#D1D5DB',
    textAlign: 'center',
    lineHeight: 1.6,
    fontWeight: 'light',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  statBox: {
    backgroundColor: '#111111',
    padding: 16,
    borderRadius: 12,
    width: '48%',
    marginBottom: 12,
    border: '1px solid #222222',
  },
  statLabel: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  statValue: {
    fontSize: 20,
    color: '#FFFFFF',
    fontWeight: 'light',
  },
  weekCard: {
    backgroundColor: '#111111',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    border: '1px solid #222222',
  },
  weekHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  weekTitle: {
    fontSize: 14,
    color: '#FFFFFF',
  },
  weekBadge: {
    backgroundColor: '#1A1A1A',
    padding: '4 8',
    borderRadius: 4,
    border: '1px solid #333333',
  },
  weekBadgeText: {
    fontSize: 10,
    color: '#EAB308',
  },
  progressBar: {
    height: 4,
    backgroundColor: '#222222',
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#EAB308',
  },
  dayCard: {
    backgroundColor: '#111111',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderLeft: '2px solid #222222',
  },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  dayTitle: {
    fontSize: 12,
    color: '#FFFFFF',
  },
  dayDate: {
    fontSize: 9,
    color: '#6B7280',
  },
  dayReason: {
    fontSize: 10,
    color: '#D1D5DB',
    lineHeight: 1.5,
  },
  insightCard: {
    backgroundColor: '#111111',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    border: '1px solid #222222',
  },
  insightText: {
    fontSize: 10,
    color: '#D1D5DB',
    lineHeight: 1.6,
  },
  achievementCard: {
    backgroundColor: '#111111',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    border: '1px solid #333333',
  },
  achievementTitle: {
    fontSize: 12,
    color: '#EAB308',
    marginBottom: 6,
  },
  achievementDesc: {
    fontSize: 10,
    color: '#9CA3AF',
    lineHeight: 1.4,
  },
  greenText: { color: '#10B981' },
  redText: { color: '#EF4444' },
  yellowText: { color: '#EAB308' },
});

// [Translations mapping kept exactly the same]
const translations = {
  de: {
    planSummary: 'Plan-Zusammenfassung', completedPlan: 'Abgeschlossener Plan', preparedFor: 'Erstellt für',
    planPeriod: 'Plan-Zeitraum', weightChange: 'Gewichtsänderung', completion: 'Abschluss',
    adherence: 'Einhaltung', goalStatus: 'Zielstatus', achieved: 'Erreicht', inProgress: 'In Bearbeitung',
    generatedBy: 'Erstellt von NutriBot', summaryOverview: 'Zusammenfassung', startWeight: 'Startgewicht',
    endWeight: 'Endgewicht', daysCompleted: 'Tage abgeschlossen', mealsLogged: 'Mahlzeiten protokolliert',
    avgCalories: 'Ø Kalorien/Tag', avgProtein: 'Ø Protein/Tag', avgCarbs: 'Ø Kohlenhydrate/Tag',
    avgFat: 'Ø Fett/Tag', weeklyBreakdown: 'Wöchentliche Übersicht', week: 'Woche',
    adherenceLabel: 'Einhaltung', weightChangeLabel: 'Gewichtsänderung', performanceHighlights: 'Leistungs-Highlights',
    bestDays: 'Beste Tage', worstDays: 'Herausfordernde Tage', day: 'Tag', insights: 'KI-Einblicke',
    achievements: 'Errungenschaften', congratulations: 'Herzlichen Glückwunsch!',
    completionMessage: 'Du hast deinen Plan mit {{completion}}% Abschluss und {{adherence}}% Einhaltung beendet. Großartige Arbeit!',
    generatedOn: 'Erstellt am', loseWeight: 'Abnehmen', buildMuscle: 'Muskeln aufbauen', maintain: 'Gewicht halten',
  },
  en: {
    planSummary: 'Plan Summary', completedPlan: 'Completed Plan', preparedFor: 'Prepared for',
    planPeriod: 'Plan Period', weightChange: 'Weight Change', completion: 'Completion',
    adherence: 'Adherence', goalStatus: 'Goal Status', achieved: 'Achieved', inProgress: 'In Progress',
    generatedBy: 'Generated by NutriBot', summaryOverview: 'Summary Overview', startWeight: 'Start Weight',
    endWeight: 'End Weight', daysCompleted: 'Days Completed', mealsLogged: 'Meals Logged',
    avgCalories: 'Avg Calories/Day', avgProtein: 'Avg Protein/Day', avgCarbs: 'Avg Carbs/Day',
    avgFat: 'Avg Fat/Day', weeklyBreakdown: 'Weekly Breakdown', week: 'Week',
    adherenceLabel: 'Adherence', weightChangeLabel: 'Weight Change', performanceHighlights: 'Performance Highlights',
    bestDays: 'Best Days', worstDays: 'Challenging Days', day: 'Day', insights: 'AI Insights',
    achievements: 'Achievements', congratulations: 'Congratulations!',
    completionMessage: 'You completed your plan with {{completion}}% completion and {{adherence}}% adherence. Great work!',
    generatedOn: 'Generated on', loseWeight: 'Lose Weight', buildMuscle: 'Build Muscle', maintain: 'Maintain Weight',
  },
};

export const PlanSummaryDocument: React.FC<PlanSummaryDocumentProps> = ({
  summary, plan, userProfile, language = 'en',
}) => {
  const t = translations[language];
  const userName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
  
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };
  
  const planEndDate = new Date(plan.plan_start_date);
  planEndDate.setDate(planEndDate.getDate() + plan.duration_days);

  const completionMessage = t.completionMessage
    .replace('{{completion}}', Math.round(summary.completion_percentage).toString())
    .replace('{{adherence}}', Math.round(summary.meal_adherence_percentage).toString());

  return (
    <Document>
      {/* Cover Page */}
      <Page size="A4" style={styles.page}>
        <View style={styles.coverPage}>
          <Text style={styles.subtitle}>{t.completedPlan}</Text>
          <Text style={styles.title}>{t.planSummary}</Text>
          {userName && (
            <Text style={styles.preparedFor}>{t.preparedFor} {userName}</Text>
          )}
          
          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t.planPeriod}</Text>
              <Text style={styles.infoValue}>
                {formatDate(plan.plan_start_date)} - {formatDate(planEndDate.toISOString())}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t.weightChange}</Text>
              <Text style={[styles.infoValue, summary.weight_change < 0 ? styles.greenText : {}]}>
                {summary.weight_change > 0 ? '+' : ''}{summary.weight_change.toFixed(1)} kg
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t.completion}</Text>
              <Text style={styles.infoValue}>{Math.round(summary.completion_percentage)}%</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t.adherence}</Text>
              <Text style={styles.infoValue}>{Math.round(summary.meal_adherence_percentage)}%</Text>
            </View>
            <View style={[styles.infoRow, { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }]}>
              <Text style={styles.infoLabel}>{t.goalStatus}</Text>
              <Text style={styles.infoValue}>
                {summary.goal_achieved ? t.achieved : t.inProgress}
              </Text>
            </View>
          </View>
          
          <Text style={styles.footer}>{t.generatedBy}</Text>
          <Text style={{ ...styles.footer, bottom: 25, fontSize: 7 }}>
            {t.generatedOn} {formatDate(summary.generated_at)}
          </Text>
        </View>
      </Page>

      {/* Summary Statistics Page */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.sectionTitle}>{t.summaryOverview}</Text>
        
        <View style={styles.statsGrid}>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.startWeight}</Text>
            <Text style={styles.statValue}>{summary.start_weight.toFixed(1)} kg</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.endWeight}</Text>
            <Text style={styles.statValue}>{summary.end_weight.toFixed(1)} kg</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.daysCompleted}</Text>
            <Text style={styles.statValue}>{summary.days_completed}/{summary.total_days}</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.mealsLogged}</Text>
            <Text style={styles.statValue}>{summary.meals_logged}/{summary.total_meals}</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.avgCalories}</Text>
            <Text style={styles.statValue}>{Math.round(summary.avg_calories)} kcal</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.avgProtein}</Text>
            <Text style={styles.statValue}>{Math.round(summary.avg_protein)}g</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.avgCarbs}</Text>
            <Text style={styles.statValue}>{Math.round(summary.avg_carbs)}g</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>{t.avgFat}</Text>
            <Text style={styles.statValue}>{Math.round(summary.avg_fat)}g</Text>
          </View>
        </View>

        <View style={styles.highlightBox}>
          <Text style={styles.highlightText}>{completionMessage}</Text>
        </View>
        <Text style={styles.footer}>{t.generatedBy}</Text>
      </Page>

      {/* Weekly Breakdown Page */}
      {summary.weekly_stats && summary.weekly_stats.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>{t.weeklyBreakdown}</Text>
          {summary.weekly_stats.map((week, index) => (
            <View key={index} style={styles.weekCard}>
              <View style={styles.weekHeader}>
                <Text style={styles.weekTitle}>{t.week} {week.week}</Text>
                <View style={styles.weekBadge}>
                  <Text style={styles.weekBadgeText}>
                    {week.weight_change > 0 ? '+' : ''}{week.weight_change.toFixed(1)} kg
                  </Text>
                </View>
              </View>
              <View style={[styles.infoRow, { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }]}>
                <Text style={styles.infoLabel}>{t.adherenceLabel}</Text>
                <Text style={styles.infoValue}>{week.adherence}%</Text>
              </View>
              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${week.adherence}%` }]} />
              </View>
            </View>
          ))}
          <Text style={styles.footer}>{t.generatedBy}</Text>
        </Page>
      )}

      {/* Performance Highlights Page */}
      {(summary.best_days?.length > 0 || summary.worst_days?.length > 0) && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>{t.performanceHighlights}</Text>
          
          {summary.best_days && summary.best_days.length > 0 && (
            <>
              <Text style={[styles.sectionTitle, { fontSize: 14, marginTop: 10, borderBottom: 'none' }]}>
                {t.bestDays}
              </Text>
              {summary.best_days.map((day, index) => (
                <View key={index} style={[styles.dayCard, { borderLeftColor: '#10B981' }]}>
                  <View style={styles.dayHeader}>
                    <Text style={styles.dayTitle}>{t.day} {day.day_number}</Text>
                    <Text style={styles.dayDate}>{formatDate(day.date)}</Text>
                  </View>
                  <Text style={styles.dayReason}>{day.reason}</Text>
                </View>))}
            </>
          )}

          {summary.worst_days && summary.worst_days.length > 0 && (
            <>
              <Text style={[styles.sectionTitle, { fontSize: 14, marginTop: 20, borderBottom: 'none' }]}>
                {t.worstDays}
              </Text>
              {summary.worst_days.map((day, index) => (
                <View key={index} style={[styles.dayCard, { borderLeftColor: '#F59E0B' }]}>
                  <View style={styles.dayHeader}>
                    <Text style={styles.dayTitle}>{t.day} {day.day_number}</Text>
                    <Text style={styles.dayDate}>{formatDate(day.date)}</Text>
                  </View>
                  <Text style={styles.dayReason}>{day.reason}</Text>
                </View>
              ))}
            </>
          )}
          <Text style={styles.footer}>{t.generatedBy}</Text>
        </Page>
      )}

      {/* Insights Page */}
      {summary.insights && summary.insights.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>{t.insights}</Text>
          {summary.insights.map((insight, index) => (
            <View key={index} style={styles.insightCard}>
              <Text style={styles.insightText}>{insight.text}</Text>
            </View>
          ))}
          <Text style={styles.footer}>{t.generatedBy}</Text>
        </Page>
      )}

      {/* Achievements Page */}
      {summary.achievements_unlocked && summary.achievements_unlocked.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>{t.achievements}</Text>
          <View style={styles.highlightBox}>
            <Text style={[styles.highlightText, { fontSize: 14, color: '#EAB308' }]}>
              {t.congratulations}
            </Text>
          </View>
          {summary.achievements_unlocked.map((achievement, index) => (
            <View key={index} style={styles.achievementCard}>
              <Text style={styles.achievementTitle}>{achievement.title}</Text>
              <Text style={styles.achievementDesc}>{achievement.description}</Text>
            </View>
          ))}
          <Text style={styles.footer}>{t.generatedBy}</Text>
        </Page>
      )}
    </Document>
  );
};